import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";

export function coerceScalar(raw) {
  const s = String(raw ?? "").trim();
  if (s === "true") return true;
  if (s === "false") return false;
  if (s === "null") return null;
  if (s !== "" && !Number.isNaN(Number(s)) && /^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return s;
}

export function parseFilterValue(value, op) {
  if (op === "in" || op === "not-in" || op === "array-contains-any") {
    return String(value)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 10)
      .map(coerceScalar);
  }
  return coerceScalar(value);
}

export function buildConditionQuery(collectionName, filter, extraConstraints = []) {
  const field = String(filter.field || "").trim();
  const op = filter.op || "==";
  if (!field) throw new Error("Field is required");

  const parsed = parseFilterValue(filter.value, op);
  const constraints = [where(field, op, parsed)];

  if (["!=", "<", "<=", ">", ">="].includes(op)) {
    constraints.push(orderBy(field));
  }

  constraints.push(...extraConstraints);
  return query(collection(db, collectionName.trim()), ...constraints);
}

export async function countMatchingDocuments(collectionName, filter) {
  const snap = await getCountFromServer(buildConditionQuery(collectionName, filter));
  return snap.data().count;
}

export async function fetchMatchingPage(collectionName, filter, { pageSize = 50, cursor = null } = {}) {
  const col = collectionName.trim();
  const fieldName = String(filter.field || "").trim();
  const opName = filter.op || "==";
  const parsed = parseFilterValue(filter.value, opName);
  const constraints = [where(fieldName, opName, parsed)];
  if (["!=", "<", "<=", ">", ">="].includes(opName)) {
    constraints.push(orderBy(fieldName));
  }
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize));

  const snap = await getDocs(query(collection(db, col), ...constraints));
  const rows = snap.docs.map((d) => ({ id: d.id, data: d.data() || {}, _snap: d }));
  return {
    rows: rows.map(({ id, data }) => ({ id, data })),
    cursor: rows.length ? rows[rows.length - 1]._snap : null,
    hasMore: rows.length === pageSize,
  };
}

export async function discoverCollectionFields(collectionName, sampleSize = 40) {
  const snap = await getDocs(query(collection(db, collectionName.trim()), limit(sampleSize)));
  const fields = new Set(["id"]);
  snap.docs.forEach((d) => {
    Object.keys(d.data() || {}).forEach((k) => fields.add(k));
  });
  return [...fields].sort();
}
